import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { SearchContact } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

const DataNotAvailable = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Typography className={classes.notAvailableText}>{t('dataNotAvailable')}</Typography>
    </Stack>
  );
};

interface Props {
  contacts: SearchContact[] | undefined;
}

const ContactsPreviewTable: React.FC<Props> = ({ contacts }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);

  const MAX_CONTACTS_PER_PAGE = 10;

  const getVisibleColumns = () => {
    const fields = {
      name: false,
      company: false,
      jobTitle: false,
      url: false,
    };
    contacts?.forEach((contact) => {
      if (contact.name) fields.name = true;
      if (contact.company) fields.company = true;
      if (contact.jobTitle) fields.jobTitle = true;
      if (contact.url) fields.url = true;
    });
    return fields;
  };

  const visibleColumns = getVisibleColumns();
  const paginatedContacts = contacts?.slice((page - 1) * MAX_CONTACTS_PER_PAGE, page * MAX_CONTACTS_PER_PAGE) || [];

  const handleChangePage = (event: any, value: number) => {
    setPage(value);
  };

  return (
    <Stack className={classes.tableContainer}>
      <div>
        <div className={classes.previewHeader}>
          <Typography className={classes.regularText}>
            <strong>{t('listPreview')}:</strong>
          </Typography>
        </div>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.tableHeadRow}>
                {visibleColumns.name && <TableCell sx={{ minWidth: '250px' }}>{t('name')}</TableCell>}
                {visibleColumns.company && <TableCell sx={{ minWidth: '250px' }}>{t('company')}</TableCell>}
                {visibleColumns.jobTitle && <TableCell sx={{ minWidth: '250px' }}>{t('jobTitle')}</TableCell>}
                {visibleColumns.url && <TableCell sx={{ minWidth: '125px' }}>{t('linkedinUrl')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedContacts.map((contact, index) => (
                <TableRow key={index}>
                  {visibleColumns.name && <TableCell>{contact.name || ''}</TableCell>}
                  {visibleColumns.company && <TableCell>{contact.company || <DataNotAvailable />}</TableCell>}
                  {visibleColumns.jobTitle && <TableCell>{contact.jobTitle || <DataNotAvailable />}</TableCell>}
                  {visibleColumns.url && (
                    <TableCell>
                      {contact.url ? (
                        <PaiLink
                          text={t('open')}
                          size={'md'}
                          variant={'primary'}
                          IconComponent={<OpenInNewIcon />}
                          href={contact.url}
                          target={'_blank'}
                        />
                      ) : (
                        <DataNotAvailable />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div className={classes.footer}>
        <PaiPagination
          total={contacts?.length || 0}
          hidden={!contacts?.length || contacts?.length <= MAX_CONTACTS_PER_PAGE}
          itemsPerPage={MAX_CONTACTS_PER_PAGE}
          page={page}
          handleChangePage={handleChangePage}
        />
      </div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    height: '100%',
    width: '100%',
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '12px',
    overflow: 'hidden',
  },
  previewHeader: {
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9,
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
  },
  notAvailableText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.light,
  },
  footer: {
    padding: '8px 16px',
  },
}));

export default ContactsPreviewTable;
